/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import { useCachedValueWithCleanup, useCurrentCallback } from "abreact/utils/AbHooks";
import { IdleTimer } from "abweb/utils/IdleTimer";
import { KeyValue } from "abweb/utils/KeyboardUtils";
import { Observer } from "mobx-react";
import React, { useCallback } from "react";

/**
 * The following situations are considered the end of an activity window:
 * - The user makes edits and then pauses for a time without shifting focus
 * - The user makes edits and then transfers focus out of this control
 * - The user pauses their typing for activityWindowMillis
 */
export function AbDebouncer(props) {
  const isActive = React.useRef(false);
  const idleTimerRef = React.useRef();
  const {
    onActivityWindowStart,
    onActivityWindowEnd,
    onChange
  } = props;
  const ensureActivityWindowStarted = useCallback(() => {
    if (!isActive.current) {
      isActive.current = true;
      onActivityWindowStart?.();
    }
  }, [onActivityWindowStart]);

  /**
   * Intercept the onChange in order to issue a {@link ensureActivityWindowStarted}, starting the {@link idleTimer} if
   * necessary. Otherwise, just record that some activity has occurred, restarting the {@link idleTimer}.
   */
  const changeHandler = useCallback(value => {
    ensureActivityWindowStarted();
    idleTimerRef.current?.activityHappened();
    onChange?.(value);
  }, [ensureActivityWindowStarted, onChange]);

  // Uses useCurrentCallback because it is called when the IdleTimer below expires. If this isn't a ref,
  // the function called from idleTimer becomes stale.
  const ensureActivityWindowEnded = useCurrentCallback(() => {
    if (isActive.current) {
      isActive.current = false;
      onActivityWindowEnd?.();
      idleTimerRef.current?.cancelAnyActivity();
    }
  });
  const keyDownHandler = useCallback(e => {
    if (e.key === KeyValue.ENTER) ensureActivityWindowEnded();
  }, [ensureActivityWindowEnded]);
  const enterHandler = useCallback(() => {
    ensureActivityWindowEnded();
  }, [ensureActivityWindowEnded]);
  const activityWindowMillis = props.activityWindowMillis ?? AbDebouncer.DEFAULT_ACTIVITY_WINDOW_MILLIS;
  idleTimerRef.current = useCachedValueWithCleanup(() => new IdleTimer(activityWindowMillis, ensureActivityWindowEnded), _ => ensureActivityWindowEnded(), [activityWindowMillis]);
  const renderProps = {
    onChange: changeHandler,
    onBlur: ensureActivityWindowEnded,
    onKeyDown: keyDownHandler,
    onEnter: enterHandler
  };
  return /*#__PURE__*/React.createElement(Observer, {
    render: () => props.render(renderProps)
  });
}
(function (_AbDebouncer) {
  const DEFAULT_ACTIVITY_WINDOW_MILLIS = _AbDebouncer.DEFAULT_ACTIVITY_WINDOW_MILLIS = 750;
})(AbDebouncer || (AbDebouncer = {}));
//# sourceMappingURL=AbDebouncer.js.map
